/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Suspense, useEffect, useState, useRef } from "react";
import { useLocation } from 'react-router';
import { LoadingPage } from "@pages/other/LoadingPage";
import { MainProvider } from "@layout/providers/MainProvider";
import { FF_BLOG } from "@/shared/utils/feature-flag";
import { PropertyPublicView } from "@/pages/public/PropertyPublicView";
import { PublicErrorBoundary } from "@/shared/components/PublicErrorBoundary";
import { AuthenticatedErrorBoundary } from "@/shared/components/AuthenticatedErrorBoundary";
import { HelmetProvider } from "react-helmet-async";
import { useAutoUpdate } from "@shared/hooks/use-auto-update";
import { MainRoutes } from "./App.routes";

/**
 * Hook that dynamically loads blog routes only when feature is enabled.
 * This enables tree-shaking when FF_BLOG is false.
 */
export const useBlogRoutes = () => {
  const [routes, setRoutes] = useState<ReturnType<typeof import("@features/blog").createBlogRoutes>>([]);

  useEffect(() => {
    if (FF_BLOG) {
      // Dynamic import to enable tree-shaking when feature is disabled
      import("@features/blog").then((module) => {
        setRoutes(module.createBlogRoutes());
      });
    }
  }, []);

  return routes;
};

/**
 * Invisible route observer that triggers the auto-update check whenever the
 * pathname changes, skipping the very first render so it does not fire on
 * initial mount. Renders nothing.
 */
export function AutoUpdateOnNavigation() {
  const location = useLocation();
  const { onRouteChange } = useAutoUpdate();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onRouteChange();
  }, [location.pathname, onRouteChange]);

  return null;
}

/**
 * Public-facing shell for the unauthenticated property view. Dismisses the
 * boot splash, restores normal document scrolling, and renders the public
 * property page under a Helmet provider and public error boundary.
 */
export function PublicShell() {
  useEffect(() => {
    const splash = document.getElementById('app-splash');
    if (splash) {
      splash.style.opacity = '0';
      setTimeout(() => splash.remove(), 200);
    }
    document.body.style.overflow = 'auto';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.height = 'auto';
    document.body.style.paddingTop = '0';
    document.body.style.paddingBottom = '0';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    const root = document.getElementById('root');
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
    }
  }, []);

  return (
    <HelmetProvider>
      <PublicErrorBoundary>
        <Suspense fallback={<LoadingPage />}>
          <PropertyPublicView />
        </Suspense>
      </PublicErrorBoundary>
    </HelmetProvider>
  );
}

/**
 * Authenticated application shell. Wraps the main routes in the app-wide
 * provider tree and mounts the navigation auto-update observer.
 */
export function AppShell() {
  return (
    <MainProvider>
      <AutoUpdateOnNavigation />
      <AuthenticatedErrorBoundary>
        <Suspense fallback={<LoadingPage />}>
          <MainRoutes />
        </Suspense>
      </AuthenticatedErrorBoundary>
    </MainProvider>
  );
}
